import { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { handleInputChangeWithScroll } from "../../../utils/editUtils";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeValidation,
  handleInputChangeWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Top", path: `/` },
];

function TopEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();

  const contentKey = "topContent"; // ページのキー
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");

  const [pageContent, setPageContent] = useState({}); // ページの表示データ
  const [heroHedderImage, setHeroHedderImage] = useState({}); // ヒーローヘッダー 画像
  const [aboutImage, setAboutImage] = useState({}); // アバウト 画像
  const [newvisitorBgImage, setNewvisitorBgImage] = useState({}); // 初めての方へ 背景画像
  const [studios, setStudios] = useState([]); // スタジオ一覧（subtext編集用）
  const [pcVideoSmall, setPcVideoSmall] = useState(null); // PC用動画(小)
  const [pcVideoMedium, setPcVideoMedium] = useState(null); // PC用動画(中)
  const [pcVideoLarge, setPcVideoLarge] = useState(null); // PC用動画(大)
  const [pcVideoExtraLarge, setPcVideoExtraLarge] = useState(null); // PC用動画(特大)
  const [spVideoSmall, setSpVideoSmall] = useState(null); // スマホ用動画(小)
  const [spVideoMedium, setSpVideoMedium] = useState(null); // スマホ用動画(中)
  const [spVideoLarge, setSpVideoLarge] = useState(null); // スマホ用動画(大)
  const [spVideoExtraLarge, setSpVideoExtraLarge] = useState(null); // スマホ用動画(特大)

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }
      
      setLoadingText("アップロード中");
      
      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
      
      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        // アップロード完了後に選択状態にする
        if (data.images?.[0]) {
          // APIレスポンスをMediaGallery形式に変換
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };
          
          // メディアファイル一覧を再取得
          await fetchMediaFiles();
          
          // fetchMediaFiles完了後に選択状態にする
          selectMedia(uploadedImage, true);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // トップ詳細の取得
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_top_content/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setPageContent(data);
      setHeroHedderImage(data.hero_hedder_image);
      setAboutImage(data.about_image);
      setNewvisitorBgImage(data.newvisitor_bg_image);
      setPcVideoSmall(data.pc_video_small);
      setPcVideoMedium(data.pc_video_medium);
      setPcVideoLarge(data.pc_video_large);
      setPcVideoExtraLarge(data.pc_video_extra_large);
      setSpVideoSmall(data.mobile_video_small);
      setSpVideoMedium(data.mobile_video_medium);
      setSpVideoLarge(data.mobile_video_large);
      setSpVideoExtraLarge(data.mobile_video_extra_large);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
    setLoadingText("");
  }, []);

  // スタジオ一覧の取得（subtext編集用）
  const fetchStudios = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studios/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setStudios(data);
    } catch (error) {
      console.error("スタジオ一覧の取得中にエラーが発生しました:", error);
    }
  }, []);

  useEffect(() => {
    fetchPageContentData(); // トップ詳細の取得
    fetchMediaFiles(); // 画像動画リストの取得
    fetchStudios(); // スタジオ一覧の取得
  }, [fetchPageContentData, fetchMediaFiles, fetchStudios]);

  // スタジオ配列をiframeへ送信する共通処理
  const sendStudiosToIframe = (nextStudios, eventType, studioId) => {
    const iframe = document.querySelector("iframe");
    if (!iframe || !iframe.contentWindow) return;

    const target = studioId != null ? `.edit_studio_subtext_${studioId}` : null;
    iframe.contentWindow.postMessage(
      {
        type: "STUDIOS_UPDATE",
        target,
        studios: nextStudios,
        shouldScroll: eventType === "focus",
      },
      process.env.REACT_APP_ORIGIN
    );
  };

  // スタジオsubtext入力ハンドラー
  const handleStudioSubtextChange = (studioId, value) => {
    const nextStudios = studios.map((s) =>
      s.id === studioId ? { ...s, subtext: value } : s
    );
    setStudios(nextStudios);
    sendStudiosToIframe(nextStudios, "change", studioId);
  };

  // スタジオsubtextフォーカスハンドラー（該当箇所までwebsiteをスクロール）
  const handleStudioSubtextFocus = (studioId) => {
    sendStudiosToIframe(studios, "focus", studioId);
  };

  // トップ詳細を更新
  const handlePageDataSubmit = async () => {
    // 更新するデータ
    const formData = {
      page_content: pageContent,
      hero_hedder_image: heroHedderImage.id,
      pc_video_small: pcVideoSmall ? pcVideoSmall.id : null,
      pc_video_medium: pcVideoMedium ? pcVideoMedium.id : null,
      pc_video_large: pcVideoLarge ? pcVideoLarge.id : null,
      pc_video_extra_large: pcVideoExtraLarge ? pcVideoExtraLarge.id : null,
      mobile_video_small: spVideoSmall ? spVideoSmall.id : null,
      mobile_video_medium: spVideoMedium ? spVideoMedium.id : null,
      mobile_video_large: spVideoLarge ? spVideoLarge.id : null,
      mobile_video_extra_large: spVideoExtraLarge ? spVideoExtraLarge.id : null,
      about_image: aboutImage.id,
      newvisitor_bg_image: newvisitorBgImage.id,
    };

    // バリデーション実行（pageContentと画像フィールドを組み合わせた全データを対象とする）
    const allFormData = {
      ...pageContent,
      hero_hedder_image: heroHedderImage.id,
      pc_video_small: pcVideoSmall ? pcVideoSmall.id : null,
      pc_video_medium: pcVideoMedium ? pcVideoMedium.id : null,
      pc_video_large: pcVideoLarge ? pcVideoLarge.id : null,
      pc_video_extra_large: pcVideoExtraLarge ? pcVideoExtraLarge.id : null,
      mobile_video_small: spVideoSmall ? spVideoSmall.id : null,
      mobile_video_medium: spVideoMedium ? spVideoMedium.id : null,
      mobile_video_large: spVideoLarge ? spVideoLarge.id : null,
      mobile_video_extra_large: spVideoExtraLarge ? spVideoExtraLarge.id : null,
      about_image: aboutImage.id,
      newvisitor_bg_image: newvisitorBgImage.id,
    };

    if (!executeValidation(allFormData, validationRules, fieldLabels, setMessage)) {
      return;
    }

    setLoadingText("保存中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_page_top_content/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }

      // スタジオsubtextの一括更新
      const subtextResponse = await fetch(
        `${apiUrl}/api/update_studios_subtext/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            studios: studios.map((s) => ({
              id: s.id,
              subtext: s.subtext || "",
            })),
          }),
        }
      );
      const subtextData = await subtextResponse.json();
      if (!subtextResponse.ok) {
        throw new Error(`${subtextData.message} status: ${subtextResponse.status}`);
      }

      setMessage(data.message);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
    setLoadingText("");
  };

  // バリデーションルールを定義
  const validationRules = {
    title_en: { required: true },
    title_ja: { required: true },
    about_title_en: { required: true },
    about_description: { required: true },
    newvisitor_catchcopy: { required: true },
    newvisitor_title: { required: true },
    newvisitor_description: { required: true },
    newvisitor_button_text: { required: true },
    hero_hedder_image: { required: true },
    pc_video_small: { required: true },
    pc_video_medium: { required: true },
    pc_video_large: { required: true },
    pc_video_extra_large: { required: true },
    mobile_video_small: { required: true },
    mobile_video_medium: { required: true },
    mobile_video_large: { required: true },
    mobile_video_extra_large: { required: true },
    about_image: { required: true },
    newvisitor_bg_image: { required: true },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    title_en: "タイトル(英語)",
    title_ja: "タイトル(日本語)",
    about_title_en: "アバウト タイトル(英語)",
    about_description: "アバウト 説明",
    newvisitor_catchcopy: "初めての方へ キャッチコピー",
    newvisitor_title: "初めての方へ タイトル",
    newvisitor_description: "初めての方へ 説明",
    newvisitor_button_text: "キャンペーン ボタンテキスト",
    hero_hedder_image: "ヒーローヘッダー画像",
    pc_video_small: "PC用動画(720p)",
    pc_video_medium: "PC用動画(1080p)",
    pc_video_large: "PC用動画(1440p)",
    pc_video_extra_large: "PC用動画(2160p)",
    mobile_video_small: "スマホ用動画(720p)",
    mobile_video_medium: "スマホ用動画(1080p)",
    mobile_video_large: "スマホ用動画(1440p)",
    mobile_video_extra_large: "スマホ用動画(2160p)",
    about_image: "アバウト 画像",
    newvisitor_bg_image: "初めての方へ 背景画像",
  };

  // テキスト入力ハンドラー
  const handleInputChange = (e) => {
    handleInputChangeWithValidation(
      e,
      setPageContent,
      handleInputChangeWithScroll,
      {
        contentKey,
        currentData: pageContent,
        setData: setPageContent,
      }
    );
  };

  // メディア選択ハンドラー
  const handleMediaSelect = (setMediaFunction, fieldName) => (media) => {
    // エラークリア処理
    const element = document.querySelector(`[data-field="${fieldName}"]`);
    if (element) {
      const errorClass = "validation-error";
      const labelClass = "validation-error-label";
      const wrapperClass = "form-field-wrapper";
      
      if (element.classList.contains(errorClass)) {
        element.classList.remove(errorClass);
        
        const mediaSelector = element.closest(".media-selector");
        if (mediaSelector) {
          mediaSelector.classList.remove(errorClass);
        }
        
        const wrapper = element.closest(`.${wrapperClass}`);
        if (wrapper) {
          const errorLabel = wrapper.querySelector(`.${labelClass}`);
          if (errorLabel) {
            errorLabel.remove();
          }
        }
      }
    }
    
    setMediaFunction(media);
  };

  // リセット処理
  const handleReset = () => {
    handleResetWithValidation(() => {
      fetchPageContentData();
      fetchStudios();
    });
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            {/* 編集フォーム */}
            <div className="form">
              {/* ヒーロヘッダー */}
              <section>
                <div className="area1">
                  {/* タイトル(英語) */}
                  <input
                    type="text"
                    name="title_en"
                    value={pageContent?.title_en || ""}
                    placeholder=""
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="タイトル(英語)"
                    data-required="true"
                  />

                  {/* タイトル(日本語) */}
                  <input
                    type="text"
                    name="title_ja"
                    value={pageContent?.title_ja || ""}
                    placeholder=""
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="タイトル(日本語)"
                    data-required="true"
                  />
                </div>

                {/* ヒーローヘッダー 画像/動画 */}
                <div className="img-form">
                  {/* ヒーローヘッダー画像 */}
                  <div className="h5-contents">
                    <h5>ヒーローヘッダー画像</h5>
                    <MediaSelector
                      label="ヒーローヘッダー画像"
                      aspectRatio="9:16"
                      type="image"
                      fieldName="hero_hedder_image"
                      selectedMedia={heroHedderImage}
                      onSelect={handleMediaSelect(setHeroHedderImage, "hero_hedder_image")}
                    />
                  </div>

                  {/* PC用動画(小) */}
                  <div className="h5-contents">
                    <h5>PC用動画(小)</h5>
                    <MediaSelector
                      label="PC用動画(小)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_small"
                      selectedMedia={pcVideoSmall}
                      onSelect={handleMediaSelect(setPcVideoSmall, "pc_video_small")}
                    />
                  </div>

                  {/* PC用動画(中) */}
                  <div className="h5-contents">
                    <h5>PC用動画(中)</h5>
                    <MediaSelector
                      label="PC用動画(中)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_medium"
                      selectedMedia={pcVideoMedium}
                      onSelect={handleMediaSelect(setPcVideoMedium, "pc_video_medium")}
                    />
                  </div>

                  {/* PC用動画(大) */}
                  <div className="h5-contents">
                    <h5>PC用動画(大)</h5>
                    <MediaSelector
                      label="PC用動画(大)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_large"
                      selectedMedia={pcVideoLarge}
                      onSelect={handleMediaSelect(setPcVideoLarge, "pc_video_large")}
                    />
                  </div>

                  {/* PC用動画(特大) */}
                  <div className="h5-contents">
                    <h5>PC用動画(特大)</h5>
                    <MediaSelector
                      label="PC用動画(特大)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_extra_large"
                      selectedMedia={pcVideoExtraLarge}
                      onSelect={handleMediaSelect(setPcVideoExtraLarge, "pc_video_extra_large")}
                    />
                  </div>

                  {/* スマホ用動画(小) */}
                  <div className="h5-contents">
                    <h5>スマホ用動画(小)</h5>
                    <MediaSelector
                      label="スマホ用動画(小)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_small"
                      selectedMedia={spVideoSmall}
                      onSelect={handleMediaSelect(setSpVideoSmall, "mobile_video_small")}
                    />
                  </div>

                  {/* スマホ用動画(中) */}
                  <div className="h5-contents">
                    <h5>スマホ用動画(中)</h5>
                    <MediaSelector
                      label="スマホ用動画(中)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_medium"
                      selectedMedia={spVideoMedium}
                      onSelect={handleMediaSelect(setSpVideoMedium, "mobile_video_medium")}
                    />
                  </div>

                  {/* スマホ用動画(大) */}
                  <div className="h5-contents">
                    <h5>スマホ用動画(大)</h5>
                    <MediaSelector
                      label="スマホ用動画(大)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_large"
                      selectedMedia={spVideoLarge}
                      onSelect={handleMediaSelect(setSpVideoLarge, "mobile_video_large")}
                    />
                  </div>

                  {/* スマホ用動画(特大) */}
                  <div className="h5-contents">
                    <h5>スマホ用動画(特大)</h5>
                    <MediaSelector
                      label="スマホ用動画(特大)"
                      aspectRatio="9:16"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_extra_large"
                      selectedMedia={spVideoExtraLarge}
                      onSelect={handleMediaSelect(setSpVideoExtraLarge, "mobile_video_extra_large")}
                    />
                  </div>
                </div>
              </section>

              {/* アバウト */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      {/* アバウト タイトル(英語) */}
                      <input
                        type="text"
                        className="input1"
                        name="about_title_en"
                        value={pageContent?.about_title_en || ""}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="アバウト タイトル(英語)"
                        data-required="true"
                      />

                      {/* アバウト 説明 */}
                      <textarea
                        className="input2"
                        name="about_description"
                        value={pageContent?.about_description || ""}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="アバウト 説明"
                        data-required="true"
                      />

                      {/* アバウト 画像 */}
                      <div className="input3 img-form">
                        <MediaSelector
                          label="アバウト 画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="about_image"
                          selectedMedia={aboutImage}
                          onSelect={handleMediaSelect(setAboutImage, "about_image")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 初めての方へ */}
              <section>
                <div className="area1">
                  {/* 初めての方へ キャッチコピー */}
                  <input
                    type="text"
                    name="newvisitor_catchcopy"
                    value={pageContent?.newvisitor_catchcopy || ""}
                    placeholder=""
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="初めての方へ キャッチコピー"
                    data-required="true"
                  />

                  {/* 初めての方へ タイトル */}
                  <input
                    type="text"
                    name="newvisitor_title"
                    value={pageContent?.newvisitor_title || ""}
                    placeholder=""
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="初めての方へ タイトル"
                    data-required="true"
                  />

                  {/* 初めての方へ 説明 */}
                  <textarea
                    className="input2"
                    name="newvisitor_description"
                    value={pageContent?.newvisitor_description || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="初めての方へ 説明"
                    data-required="true"
                  />

                  {/* キャンペーン ボタンテキスト */}
                  <input
                    type="text"
                    name="newvisitor_button_text"
                    value={pageContent?.newvisitor_button_text || ""}
                    placeholder=""
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="キャンペーン ボタンテキスト"
                    data-required="true"
                  />

                  {/* キャンペーン タイトル */}
                  {/* <input
                    type="text"
                    name="campaign_title"
                    value={pageContent?.campaign_title || ""}
                    placeholder=""
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="キャンペーン タイトル"
                    data-required="true"
                  /> */}
                </div>
                <div className="img-form">
                  {/* 初めての方へ 背景画像 */}
                  <MediaSelector
                    label="初めての方へ 背景画像"
                    aspectRatio="9:16"
                    type="image"
                    fieldName="newvisitor_bg_image"
                    selectedMedia={newvisitorBgImage}
                    onSelect={handleMediaSelect(setNewvisitorBgImage, "newvisitor_bg_image")}
                  />
                </div>
              </section>

              {/* スタジオ 短い説明（Topページのスタジオカードに表示） */}
              <section>
                <div className="area1" style={{ width: "100%" }}>
                  <h5>スタジオ 短い説明（Topページのスタジオカードに表示）</h5>
                  {studios.map((studio) => (
                    <label
                      key={studio.id}
                      style={{ display: "block", width: "100%", marginBottom: "0.75em" }}
                    >
                      <span style={{ display: "block", fontWeight: "bold", marginBottom: "0.25em" }}>
                        {studio.name}
                      </span>
                      <input
                        type="text"
                        value={studio.subtext || ""}
                        placeholder="例:長与駅から徒歩5分。XXX小学校すぐそば"
                        maxLength={255}
                        onChange={(e) => handleStudioSubtextChange(studio.id, e.target.value)}
                        onFocus={() => handleStudioSubtextFocus(studio.id)}
                        style={{ width: "100%", boxSizing: "border-box" }}
                      />
                    </label>
                  ))}
                </div>
              </section>
            </div>
            {/* 送信ボタン */}
            <div className="submit-area">
              <button type="reset" onClick={handleReset}>
                Reset
              </button>
              <button type="submit" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>
            {/* メディア / オーバーレイ */}
            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>
      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div> <p>{loadingText}...</p>
          </div>
        </div>
      )}
      {/* プレビュー */}
      <Iframe path={"/"} data={pageContent} contentKey={contentKey} />
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function TopEditPage() {
  return (
    <MediaProvider>
      <TopEditPageContent />
    </MediaProvider>
  );
}
